import { motion } from 'motion/react'
import {
	type ComponentPropsWithoutRef,
	forwardRef,
	useEffect,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import MarketingImage from '#app/components/marketing/image.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useIsMounted } from '#app/hooks/use-is-mounted.ts'
import { useMainStore } from '#app/stores/main'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import { useDesktop } from '#app/utils/media-query.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'

interface Card {
	slug: string
	title: string
	description: string
	image: string
	link: string
	className?: string
}

interface CardProps {
	card: Card
	index: number
	heights: {
		card: number
		overlap: number
	}
}

const StackedCard = forwardRef<
	HTMLDivElement,
	CardProps & ComponentPropsWithoutRef<'div'>
>(({ card, index, heights, ...props }, ref) => {
	const { t } = useTranslation('common')
	const mounted = useIsMounted()

	return (
		<div
			{...props}
			ref={ref}
			data-height={heights.card}
			className={cn(
				'grid grid-cols-1 gap-md border-t border-current bg-transparent py-xxl md:grid-cols-2 md:gap-xl md:py-xxxl xl:grid-cols-3',
				props.className,
			)}
		>
			<div
				className={
					'grid grid-cols-1 items-start gap-2 md:grid-cols-[1fr_50px] md:gap-4'
				}
			>
				<MarketingImage
					imageClassName="block max-h-[300px] md:min-h-[400px] min-h-[300px]"
					src={card.image}
				/>
				<div
					className={
						'row-start-1 flex items-center gap-2 text-xl font-extralight md:row-start-auto'
					}
				>
					<span>/</span>
					{/* fill with 0 when only one digit */}
					<span>{index + 1 < 10 ? `0${index + 1}` : index + 1}</span>
					<span>/</span>
				</div>
			</div>
			<div
				className={'flex w-full flex-col gap-4 lg:col-span-2 lg:pr-lg xl:pr-0'}
			>
				<Text type={'serif'} weight={'light'} as={'h3'} size={'h3'}>
					<SplitText
						element={'words'}
						as={'span'}
						delay={0.2}
						className="line-clamp-4 lg:line-clamp-3"
					>
						{card.title}
					</SplitText>
				</Text>
				<Text
					as={'p'}
					size={'p'}
					weight={'extralight'}
					className={'line-clamp-5 lg:line-clamp-3'}
					dangerouslySetInnerHTML={{
						__html: mounted ? `${card.description}` : '',
					}}
				></Text>
				<Button
					asChild
					variant={'link'}
					size={'sm'}
					className={
						'self-end justify-self-end lowercase tracking-[0.2em] text-brand-earth'
					}
				>
					<LocalizedLink
						to={routes.public.blog.post.route({
							slug: card.slug,
						})}
					>
						{t('common:actions.viewMore')}
					</LocalizedLink>
				</Button>
			</div>
		</div>
	)
})

export default function StackedCardsComponent({
	indexOffset = 0,
	cards,
	className,
}: {
	indexOffset?: number
	cards: Card[]
	className?: string
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const firstCardRef = useRef<HTMLLIElement>(null)
	const lastCardRef = useRef<HTMLLIElement>(null)
	const { height: windowHeight } = useWindowSize()
	const [cardHeight, setCardHeight] = useState(0)
	const [overlapHeight, setOverlapHeight] = useState(0)
	const isDesktop = useDesktop()
	const backgroundColor = useMainStore.use.backgroundColor()
	const mounted = useIsMounted()

	useEffect(() => {
		if (!firstCardRef.current || !containerRef.current || !lastCardRef.current)
			return

		let measurementCount = 0
		const maxMeasurements = 250 // Prevent infinite loops

		const resizeObserver = new ResizeObserver(() => {
			if (measurementCount >= maxMeasurements) return
			measurementCount++

			// Small delay to let all layout settle
			setTimeout(() => {
				const cardHeight = Math.max(
					firstCardRef.current!.getBoundingClientRect().height,
					lastCardRef.current!.getBoundingClientRect().height,
				)
				setCardHeight(cardHeight)

				const totalHeight = cardHeight * cards.length
				const availableFoldingHeight = windowHeight - cardHeight

				const overlapPerCard = availableFoldingHeight / (cards.length - 1)
				setOverlapHeight(overlapPerCard)

				containerRef.current!.style.height = `${totalHeight}px`
			}, 100)
		})

		resizeObserver.observe(firstCardRef.current)
		resizeObserver.observe(lastCardRef.current)

		return () => resizeObserver.disconnect()
	}, [cards.length, windowHeight])

	return (
		<motion.div
			ref={containerRef}
			className={cn('container mx-auto max-w-[1920px]', className)}
		>
			<ul>
				{cards.map((card, index) => (
					<motion.li
						key={card.slug}
						ref={
							index === 0
								? firstCardRef
								: index === cards.length - 1
									? lastCardRef
									: null
						}
						className={cn(
							mounted &&
								isDesktop &&
								`sticky transition-colors duration-1000 ease-in-out`,
							card.className,
						)}
						style={{
							top: `${overlapHeight * index}px`,
							backgroundColor: backgroundColor,
						}}
					>
						<StackedCard
							card={card}
							heights={{
								card: cardHeight,
								overlap: overlapHeight,
							}}
							index={index + indexOffset}
						/>
					</motion.li>
				))}
			</ul>
		</motion.div>
	)
}
